import React, { useState } from "react";
import { Search, Bell, CircleQuestionMark } from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/input";
import { useAuth } from "../auth/AuthContext"; // adjust path
import { useNavigate, useLocation } from "react-router-dom";
import NotificationSidebar from "./NotificationSidebar";
import AestheticSearchOverlay from "./aesthetics/AestheticSearchOverlay";

const AestheticsNavbar = ({ onMobileMenuClick }: { onMobileMenuClick: () => void }) => {
  const [searchValue, setSearchValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationSidebarOpen, setNotificationSidebarOpen] = useState(false);
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const notificationCount = 3;
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="px-6 py-3 shadow-lg" style={{ backgroundColor: "#EAEAF4" }}>
      <div className="flex items-center justify-between w-full">
        {/* Logo */}
        <div
          className="flex items-center cursor-pointer"
          // onClick={() => navigate("/")}
        >
          {/* <img src="/logo.svg" alt="Glossa Logo" className="h-9 w-auto" /> */}
          <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOverlayOpen(true)}
              className=" bg-white hover:bg-white/10 px-4 py-2 shadow-sm justify-start rounded-3xl flex items-center w-auto md:w-[300px] lg:w-[400px] xl:w-[500px]"
              title="Search"
            >
              <Search className="w-7 h-7 text-purple-900" />
              <span className="text-gray-500 hidden md:block">Search...</span>
            </Button>
        </div>

        <div className="flex justify-end items-center relative">
          {/* Search Trigger Icon */}

          {/* Notification Button */}
          <Button
            variant="ghost"
            size="icon"
            // onClick={() => navigate("/")}
            className="hover:bg-white/10 rounded-md flex items-center justify-center"
          >
            <Bell />
          </Button>
          {/* Help Button */}
          <Button
            variant="ghost"
            size="icon"
            // onClick={() => navigate("/")}
            className="hover:bg-white/10 rounded-md flex items-center justify-center"
          >
            <CircleQuestionMark />
          </Button>

          {/* User Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="hover:bg-white/10 rounded-md w-auto px-3 h-10 flex items-center justify-center gap-2"
            >
              <span>{user.name || 'User'}</span>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center border-2 border-purple-200">
                            <span className="text-lg font-bold text-purple-600">
                              {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
            </Button>

            {/* Dropdown */}
            {menuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />

                {/* Enhanced Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  {user ? (
                    <>
                      {/* User Info Section */}
                      <div className="px-4 py-4 bg-white border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center border-2 border-purple-200">
                            <span className="text-lg font-bold text-purple-600">
                              {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate text-gray-900">
                              {user.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {user.email || ''}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            navigate('/aesthetics/settings');
                            setMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Settings</span>
                        </button>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200"></div>

                      {/* Sign Out Button */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            logout();
                            setMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-3 font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate("/login");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          navigate("/signup");
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Sign Up
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <NotificationSidebar
        isOpen={notificationSidebarOpen}
        onClose={() => setNotificationSidebarOpen(false)}
      />
      <AestheticSearchOverlay
        isOpen={searchOverlayOpen}
        onClose={() => setSearchOverlayOpen(false)}
      />
    </nav>
  );
};

export default AestheticsNavbar;